/** @format */

import React from "react";
import {
    Box,
    Typography,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { IRecoveryTrends } from "../../../utility/types/collectTypes";
import { colors } from "../../../utility/types/constant";
import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
} from "../styles";

// ─────────────────────────────────────────────────────────────────────────
// RecoveryTrendsPanel — time-series view of collections posture built from
// the daily snapshots the `collectDailySnapshot` cron freezes each night
// (Stage 2H). Plots three stories on one chart:
//   • Health score (line, right 0–100 axis) — is the book getting healthier?
//   • Recovered $ per day (columns, left currency axis) — are we collecting?
//   • Overdue balance (line, left currency axis) — is risk shrinking?
// Reads from GET /collect/analytics/trends?days=N.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    trends: IRecoveryTrends | null;
    isLoading: boolean;
    days: number;
    onDaysChange: (days: number) => void;
}

function fmtCompact(n: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            notation: "compact",
            maximumFractionDigits: 1,
        }).format(n || 0);
    } catch {
        return `$${(n || 0).toFixed(0)}`;
    }
}

function fmtDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });
}

const RANGE_OPTIONS = [
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: 90, label: "Last 90 days" },
];

const RecoveryTrendsPanel: React.FC<Props> = ({
    trends,
    isLoading,
    days,
    onDaysChange,
}) => {
    const points = trends?.points ?? [];
    // Snapshots are frozen once per day, so a brand-new workspace legitimately
    // has zero history. Treat "no points" as an informative empty state.
    const hasData = points.length > 0;

    const categories = points.map((p) => fmtDate(p.date));
    const healthSeries = points.map((p) => Math.round(p.healthScore || 0));
    const recoveredSeries = points.map((p) => Math.round(p.recoveredAmount || 0));
    const overdueSeries = points.map((p) => Math.round(p.overdueBalance || 0));

    // Currency is not part of the trends payload; default to USD to match the
    // rest of the dashboard's snapshot-derived widgets.
    const currency = "USD";

    const options: ApexOptions = {
        chart: {
            type: "line",
            toolbar: { show: false },
            fontFamily: "'Montserrat', sans-serif",
            stacked: false,
        },
        stroke: {
            width: [0, 3, 3],
            curve: "smooth",
        },
        plotOptions: {
            bar: { columnWidth: "45%", borderRadius: 3 },
        },
        colors: [colors.mainSecondary, colors.primary, "#f59e0b"],
        dataLabels: { enabled: false },
        // Always-visible dots on the two line series (index 1 & 2); the column
        // series (index 0) keeps 0 so bars aren't dotted. Without this, sparse
        // snapshot history renders as near-invisible lines.
        markers: {
            size: [0, 5, 5],
            strokeWidth: 0,
            hover: { size: 7 },
        },
        legend: {
            position: "top",
            horizontalAlign: "left",
            fontSize: "12px",
            fontWeight: 600,
            markers: { width: 10, height: 10, radius: 3 },
            itemMargin: { horizontal: 10 },
        },
        grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
        xaxis: {
            categories,
            tickAmount: Math.min(categories.length, 8),
            labels: {
                rotate: -45,
                rotateAlways: false,
                hideOverlappingLabels: true,
                style: { colors: "#6b7280", fontSize: "11px" },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: [
            {
                seriesName: "Recovered",
                min: 0,
                forceNiceScale: true,
                title: {
                    text: "Amount",
                    style: { color: "#6b7280", fontSize: "11px", fontWeight: 600 },
                },
                labels: {
                    formatter: (v: number) => fmtCompact(v, currency),
                    style: { colors: "#6b7280", fontSize: "11px" },
                },
            },
            {
                // Bind Overdue to the SAME (Recovered) money axis so both cash
                // series read against one scale. With its own seriesName Apex
                // gives Overdue a separate hidden scale, leaving the visible
                // "Amount" axis scaled only to the small daily recovered values.
                seriesName: "Recovered",
                show: false,
            },
            {
                opposite: true,
                seriesName: "Health score",
                min: 0,
                max: 100,
                title: {
                    text: "Health",
                    style: { color: "#6b7280", fontSize: "11px", fontWeight: 600 },
                },
                labels: {
                    formatter: (v: number) => `${Math.round(v)}`,
                    style: { colors: "#6b7280", fontSize: "11px" },
                },
            },
        ],
        tooltip: {
            shared: true,
            intersect: false,
            y: {
                formatter: (val: number, opts: any) => {
                    const seriesIdx = opts?.seriesIndex ?? 0;
                    if (seriesIdx === 2) return `${Math.round(val)} / 100`;
                    return fmtCompact(val, currency);
                },
            },
        },
    };

    const series = [
        { name: "Recovered", type: "column", data: recoveredSeries },
        { name: "Overdue balance", type: "line", data: overdueSeries },
        { name: "Health score", type: "line", data: healthSeries },
    ];

    return (
        <CollectSectionCard sx={{ p: 3, height: "100%" }}>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>Recovery trends</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        Daily health score, recovered cash and overdue balance over time.
                    </CollectSectionSubtitle>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        value={days}
                        onChange={(e) => onDaysChange(Number(e.target.value))}
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            borderRadius: "10px",
                            "& .MuiSelect-select": { py: 0.85 },
                        }}
                    >
                        {RANGE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </CollectSectionHeader>

            {isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                    }}
                >
                    <CircularProgress size={28} sx={{ color: colors.mainSecondary }} />
                </Box>
            ) : !hasData ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                        gap: 0.75,
                        px: 2,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: colors.primary,
                            fontSize: "0.95rem",
                        }}
                    >
                        No history yet
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#9ca3af",
                            fontSize: "0.85rem",
                            maxWidth: 360,
                        }}
                    >
                        We snapshot your collections posture once a day. Trend lines
                        will start filling in after the first overnight snapshot.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ mt: 1 }}>
                    <ReactApexChart
                        options={options}
                        series={series}
                        type="line"
                        height={340}
                    />
                </Box>
            )}
        </CollectSectionCard>
    );
};

export default RecoveryTrendsPanel;
